import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import {
  Cpu, ArrowLeftRight, Boxes, TrendingUp, Network, Sigma, Blocks,
  Radar, ScanLine, Gauge, Zap, Activity, ShieldCheck, ExternalLink, Copy, Check,
  Wallet, Layers, Database,
} from "lucide-react";

const NODES = [
  { key: "CEX", label: "CEX", icon: ArrowLeftRight, x: 200, y: 50, lx: "50%", ly: "12.5%" },
  { key: "DEX", label: "DEX", icon: Boxes, x: 330, y: 125, lx: "82.5%", ly: "31.25%" },
  { key: "FUT", label: "Futures", icon: TrendingUp, x: 330, y: 275, lx: "82.5%", ly: "68.75%" },
  { key: "CROSS", label: "Cross-Market", icon: Network, x: 200, y: 350, lx: "50%", ly: "87.5%" },
  { key: "STAT", label: "Statistical", icon: Sigma, x: 70, y: 275, lx: "17.5%", ly: "68.75%" },
  { key: "MEV", label: "MEV / On-chain", icon: Blocks, x: 70, y: 125, lx: "17.5%", ly: "31.25%" },
];

/* Central animated QCORE engine with 6 orbiting market nodes + data-flow particles */
export function QCoreEngine({ className = "" }) {
  return (
    <div className={`relative aspect-square w-full max-w-[520px] mx-auto ${className}`} data-testid="qcore-engine">
      <svg viewBox="0 0 400 400" className="absolute inset-0 h-full w-full overflow-visible">
        <defs>
          <radialGradient id="qc-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#0E9E90" stopOpacity="0.9" />
            <stop offset="70%" stopColor="#0E9E90" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#0E9E90" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="qc-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#2DD4BF" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#2DD4BF" stopOpacity="0.5" />
          </linearGradient>
        </defs>

        {/* rotating dashed rings */}
        <g className="qs-spin-slow">
          <circle cx="200" cy="200" r="150" fill="none" stroke="rgba(45,212,191,0.14)" strokeWidth="1" strokeDasharray="2 10" />
        </g>
        <g className="qs-spin-rev">
          <circle cx="200" cy="200" r="110" fill="none" stroke="rgba(45,212,191,0.10)" strokeWidth="1" strokeDasharray="1 8" />
        </g>

        {/* connecting lines + travelling data particles */}
        {NODES.map((n) => {
          const path = `M${n.x},${n.y} L200,200`;
          return (
            <g key={n.key}>
              <line x1={n.x} y1={n.y} x2="200" y2="200" stroke="url(#qc-line)" strokeWidth="1.25" />
              <circle r="2.4" fill="#2DD4BF">
                <animateMotion dur={`${2 + (n.x % 3)}s`} repeatCount="indefinite" path={path} />
                <animate attributeName="opacity" values="0;1;1;0" dur={`${2 + (n.x % 3)}s`} repeatCount="indefinite" />
              </circle>
              <circle r="1.6" fill="#00E676">
                <animateMotion dur="2.6s" begin="1s" repeatCount="indefinite" path={`M200,200 L${n.x},${n.y}`} />
              </circle>
            </g>
          );
        })}

        {/* core glow */}
        <circle cx="200" cy="200" r="90" fill="url(#qc-core)" />
        <circle cx="200" cy="200" r="46" fill="#08090F" stroke="rgba(14,158,144,0.6)" strokeWidth="1.5" />
      </svg>

      {/* HTML overlay: core label */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
        <div className="grid place-items-center h-[92px] w-[92px] rounded-full bg-[#08090F] border border-[#0E9E90]/50 qs-glow-blue">
          <Cpu size={22} strokeWidth={1.5} className="text-[#2DD4BF]" />
          <span className="mt-1 font-mono text-[11px] font-bold tracking-[0.15em] text-white">QCORE</span>
        </div>
      </div>

      {/* market node chips */}
      {NODES.map((n, i) => (
        <motion.div
          key={n.key}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 + i * 0.09, ease: [0.16, 1, 0.3, 1] }}
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{ left: n.lx, top: n.ly }}
          data-testid={`qc-node-${n.key.toLowerCase()}`}
        >
          <div className="flex flex-col items-center gap-1.5">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#0A0B10] border border-white/10 text-[#2DD4BF] shadow-[0_8px_24px_rgba(0,0,0,0.5)]">
              <n.icon size={18} strokeWidth={1.5} />
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-slate-400 whitespace-nowrap">{n.label}</span>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

const PIPELINE = [
  { icon: Radar, label: "Detect", note: "Market data streams enter Q-Core across venues." },
  { icon: ScanLine, label: "Analyze", note: "Pricing, liquidity, spreads and conditions compared." },
  { icon: Gauge, label: "Evaluate", note: "Opportunities scored against risk parameters." },
  { icon: Zap, label: "Execute", note: "Qualifying strategies run to programmed logic." },
  { icon: Activity, label: "Monitor", note: "Positions and outcomes continuously tracked." },
];

/* Inside QCore — 5-step interactive timeline */
export function InsideQCore() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % PIPELINE.length), 2600);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="inside-qcore" className="relative">
      <div className="grid md:grid-cols-5 gap-3">
        {PIPELINE.map((s, i) => {
          const on = i === active;
          return (
            <button
              key={s.label}
              onMouseEnter={() => setActive(i)}
              onClick={() => setActive(i)}
              data-testid={`qcore-step-${s.label.toLowerCase()}`}
              className={`group relative text-left rounded-xl border p-5 transition-colors duration-300 ${
                on ? "border-[#0E9E90]/50 bg-[#0E9E90]/[0.06]" : "border-white/5 bg-[#0A0B10] hover:border-white/10"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className={`grid place-items-center h-10 w-10 rounded-lg border transition-colors ${on ? "border-[#0E9E90]/50 bg-[#0E9E90]/15 text-[#2DD4BF]" : "border-white/10 bg-white/[0.02] text-slate-400"}`}>
                  <s.icon size={18} strokeWidth={1.5} />
                </span>
                <span className="font-mono text-xs text-slate-600">0{i + 1}</span>
              </div>
              <p className="font-semibold text-white text-sm">{s.label}</p>
              <p className="mt-1.5 text-xs text-slate-400 leading-relaxed">{s.note}</p>
              {on && (
                <motion.span layoutId="qcore-underline" className="absolute left-5 right-5 -bottom-px h-0.5 bg-[#0E9E90]" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* Live cross-exchange spread hook (real prices via /api/market/spreads, simulated execution) */
export function useLiveSpreads(pollMs = 12000) {
  const [state, setState] = useState({ pairs: [], venues: [], updatedAt: 0, live: false });
  useEffect(() => {
    let alive = true;
    const fetchIt = async () => {
      try {
        const { data } = await api.get("/market/spreads");
        if (alive && data?.pairs) setState({ pairs: data.pairs, venues: data.venues_online || [], updatedAt: data.updated_at, live: data.source === "live" });
      } catch { /* keep last */ }
    };
    fetchIt();
    const t = setInterval(fetchIt, pollMs);
    return () => { alive = false; clearInterval(t); };
  }, [pollMs]);
  return state;
}

const fmtP = (n) => (n >= 1000 ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : n >= 1 ? n.toFixed(3) : n.toFixed(5));
const fmtVol = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : n.toFixed(0));
const agoStr = (ts) => { const s = Math.max(0, Math.floor(Date.now() / 1000 - ts)); return s < 60 ? `${s}s ago` : `${Math.floor(s / 60)}m ago`; };

/* Live opportunity feed — real prices, simulated execution */
export function LiveTerminal() {
  const { pairs, venues, updatedAt, live } = useLiveSpreads(12000);
  return (
    <div data-testid="live-terminal" className="rounded-2xl border border-white/5 bg-[#0A0B10] overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3.5 border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <span className="qs-pulse-dot h-2 w-2 rounded-full bg-[#00E676] text-[#00E676]" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-white">Q-Core · Cross-Exchange Spreads</span>
          {live && <span className="font-mono text-[10px] text-slate-500">· {venues.join(" · ")}{updatedAt ? ` · ${agoStr(updatedAt)}` : ""}</span>}
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest rounded-full border border-[#00E676]/40 bg-[#00E676]/10 px-2.5 py-1 text-[#00E676]">Real Prices · Simulated Execution</span>
      </div>
      <div className="hidden sm:grid grid-cols-12 gap-2 px-5 py-2.5 border-b border-white/5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
        <span className="col-span-2">Pair</span>
        <span className="col-span-5">Route (buy → sell)</span>
        <span className="col-span-2 text-right">24h</span>
        <span className="col-span-2 text-right">Spread</span>
        <span className="col-span-1 text-right">Status</span>
      </div>
      <div className="divide-y divide-white/5">
        {pairs.length ? pairs.map((r) => (
          <div key={r.pair} className="grid grid-cols-3 sm:grid-cols-12 gap-2 px-5 py-3 items-center font-mono text-xs">
            <span className="col-span-1 sm:col-span-2 text-white">{r.pair}</span>
            <span className="hidden sm:block col-span-5 text-slate-400 truncate">{r.buy_venue} <span className="text-slate-600">@ {fmtP(r.buy)}</span> → {r.sell_venue} <span className="text-slate-600">@ {fmtP(r.sell)}</span></span>
            <span className={`col-span-1 sm:col-span-2 text-right ${r.change_24h >= 0 ? "text-[#00E676]" : "text-[#FF2E63]"}`}>{r.change_24h >= 0 ? "+" : ""}{r.change_24h}%</span>
            <span className="col-span-1 sm:col-span-2 text-right text-[#2DD4BF]">{r.spread_pct}%</span>
            <span className="col-span-1 text-right">
              <span className={`inline-flex items-center gap-1 ${r.status === "EXECUTABLE" ? "text-[#00E676]" : "text-amber-300"}`}>
                <span className="h-1.5 w-1.5 rounded-full bg-current" /> <span className="hidden md:inline">{r.status}</span>
              </span>
            </span>
          </div>
        )) : (
          <div className="px-5 py-10 text-center font-mono text-xs text-slate-500">Connecting to live market data…</div>
        )}
      </div>
    </div>
  );
}

/* Verify on chain teaser */
export function VerifyOnChainTeaser() {
  const [copied, setCopied] = useState(false);
  const sample = "0x9f2c…a41e · Ethereum Mainnet";
  const copy = async () => {
    try { await navigator.clipboard?.writeText("0x9f2c00000000000000000000000000000000a41e"); } catch { /* clipboard blocked in restricted contexts */ }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div data-testid="verify-onchain-teaser" className="relative overflow-hidden rounded-2xl border border-white/5 bg-gradient-to-b from-[#12131A] to-[#0A0B10] p-8 qs-aurora">
      <div className="relative">
        <div className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#00E676] mb-5">
          <ShieldCheck size={15} strokeWidth={1.5} /> On-Chain Transparency
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-white leading-tight max-w-lg">Don't take our word for it. Verify it on-chain.</h3>
        <p className="mt-4 text-sm text-slate-400 max-w-md leading-relaxed">
          Where activity is publicly recorded, transaction hashes can be independently verified on the relevant blockchain explorer.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/40 px-4 py-3 font-mono text-xs text-slate-300 flex-1">
            <span className="truncate">{sample}</span>
            <button onClick={copy} data-testid="verify-copy" className="text-slate-500 hover:text-white transition-colors">
              {copied ? <Check size={15} className="text-[#00E676]" /> : <Copy size={15} />}
            </button>
          </div>
          <a
            href="https://etherscan.io" target="_blank" rel="noopener noreferrer" data-testid="verify-explorer-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0E9E90] text-white font-medium px-6 py-3 shadow-[0_0_20px_rgba(14,158,144,0.3)] hover:bg-[#1E4BCC] transition-colors"
          >
            Verify Transaction <ExternalLink size={15} strokeWidth={2} />
          </a>
        </div>
      </div>
    </div>
  );
}

/* Full Q-Core architecture diagram — Market Data -> Engine -> Venues -> Execution -> Portfolio */
const ARCH_STAGES = [
  { key: "market", label: "Market Data", tag: "01 · Ingest", icon: Database, sub: ["CEX order books", "DEX pools", "Futures curves", "On-chain state"], desc: "Q-Core continuously ingests fragmented market data — order books, liquidity pools, funding rates and on-chain state — across hundreds of venues and multiple chains." },
  { key: "engine", label: "Q-Core AI · Opportunity Engine", tag: "02 · Analyze", icon: Cpu, sub: ["Pricing & spreads", "Liquidity depth", "Risk scoring", "Feasibility check"], desc: "The engine compares pricing relationships, liquidity depth and execution costs in parallel, scoring potential opportunities against configurable risk parameters. Quantum-inspired optimization on classical infrastructure." },
  { key: "venues", label: "CEX · DEX · Futures", tag: "03 · Route", icon: Network, sub: ["Centralized", "Decentralized", "Derivatives"], desc: "Qualifying opportunities are routed to the appropriate market layer — centralized exchanges, decentralized protocols or derivatives venues — depending on where the edge exists." },
  { key: "exec", label: "Execution / Analysis", tag: "04 · Act", icon: Zap, sub: ["Programmed logic", "Slippage guards", "Post-trade analysis"], desc: "Where configured and technically supported, strategies execute to their programmed logic with slippage and cost controls. Every action is recorded for post-trade analysis." },
  { key: "portfolio", label: "Portfolio", tag: "05 · Result", icon: Wallet, sub: ["Ledger entry", "Payout routing", "Transparency"], desc: "Outcomes settle into your portfolio and are written to an immutable ledger. Payouts route to your wallets per your configuration — fully transparent and, where public, verifiable on-chain." },
];

export function QCoreArchitecture() {
  const [active, setActive] = useState("engine");
  const cur = ARCH_STAGES.find((s) => s.key === active) || ARCH_STAGES[1];
  return (
    <div data-testid="qcore-architecture" className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 flex flex-col items-stretch">
        {ARCH_STAGES.map((s, i) => {
          const on = s.key === active;
          const isVenues = s.key === "venues";
          return (
            <div key={s.key} className="flex flex-col items-center">
              <button
                onMouseEnter={() => setActive(s.key)} onClick={() => setActive(s.key)}
                data-testid={`arch-stage-${s.key}`}
                className={`w-full text-left rounded-xl border p-4 md:p-5 transition-colors duration-300 ${on ? "border-[#0E9E90]/50 bg-[#0E9E90]/[0.07] qs-glow-blue" : "border-white/5 bg-[#0A0B10] hover:border-white/10"} ${s.key === "engine" ? "ring-1 ring-[#0E9E90]/20" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`grid place-items-center h-11 w-11 rounded-lg border shrink-0 ${on ? "border-[#0E9E90]/50 bg-[#0E9E90]/15 text-[#2DD4BF]" : "border-white/10 bg-white/[0.02] text-slate-400"}`}>
                    <s.icon size={20} strokeWidth={1.5} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{s.tag}</p>
                    <p className="font-semibold text-white text-sm md:text-base truncate">{s.label}</p>
                  </div>
                </div>
                {isVenues && (
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {["CEX", "DEX", "Futures"].map((v) => (
                      <span key={v} className="rounded-md border border-white/10 bg-black/30 py-1.5 text-center font-mono text-[10px] uppercase tracking-wide text-slate-300">{v}</span>
                    ))}
                  </div>
                )}
              </button>
              {i < ARCH_STAGES.length - 1 && (
                <svg width="24" height="34" viewBox="0 0 24 34" className="my-0.5">
                  <line x1="12" y1="0" x2="12" y2="34" stroke="rgba(45,212,191,0.35)" strokeWidth="1.5" className="qs-flow-line" />
                  <polygon points="12,34 8,26 16,26" fill="rgba(45,212,191,0.5)" />
                </svg>
              )}
            </div>
          );
        })}
      </div>

      <div className="lg:col-span-2">
        <motion.div
          key={cur.key}
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
          className="lg:sticky lg:top-28 rounded-2xl border border-white/10 bg-gradient-to-b from-[#12131A] to-[#0A0B10] p-6 qs-aurora"
        >
          <div className="relative">
            <span className="grid place-items-center h-12 w-12 rounded-xl border border-[#0E9E90]/40 bg-[#0E9E90]/10 text-[#2DD4BF] mb-4"><cur.icon size={22} strokeWidth={1.5} /></span>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#2DD4BF]">{cur.tag}</p>
            <h4 className="text-xl font-bold text-white mt-1">{cur.label}</h4>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">{cur.desc}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {cur.sub.map((x) => (
                <span key={x} className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] text-slate-300">{x}</span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* Professional per-market trading terminal — real cross-exchange spreads */
const MARKET_META = {
  cex: { label: "CEX", note: "Live cross-exchange spot spreads across OKX, Kraken and Gate." },
  dex: { label: "DEX", note: "Spot reference spreads shown. Native DEX pool feeds connect when Q-Core APIs are provisioned." },
  futures: { label: "Futures", note: "Spot reference spreads shown. Perp/basis feeds connect when Q-Core APIs are provisioned." },
};
export function TradingTerminal({ market = "cex" }) {
  const { pairs, venues, updatedAt, live } = useLiveSpreads(12000);
  const meta = MARKET_META[market] || MARKET_META.cex;
  return (
    <div data-testid={`trading-terminal-${market}`} className="rounded-2xl border border-white/5 bg-[#0A0B10] overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3.5 border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <span className="qs-pulse-dot h-2 w-2 rounded-full bg-[#00E676] text-[#00E676]" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-white">{meta.label} · Spread Log</span>
          {live && <span className="font-mono text-[10px] text-slate-500">· {venues.join(" · ")}{updatedAt ? ` · ${agoStr(updatedAt)}` : ""}</span>}
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest rounded-full border border-[#00E676]/40 bg-[#00E676]/10 px-2.5 py-1 text-[#00E676]">Real Prices · Simulated Execution</span>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[720px]">
          <div className="grid grid-cols-12 gap-2 px-5 py-2.5 border-b border-white/5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <span className="col-span-2">Pair</span>
            <span className="col-span-3">Route</span>
            <span className="col-span-2 text-right">Buy</span>
            <span className="col-span-2 text-right">Sell</span>
            <span className="col-span-1 text-right">Spread</span>
            <span className="col-span-2 text-right">Status</span>
          </div>
          <div className="divide-y divide-white/5">
            {pairs.length ? pairs.map((r) => (
              <div key={r.pair} className="grid grid-cols-12 gap-2 px-5 py-3 items-center font-mono text-xs">
                <span className="col-span-2 text-white">{r.pair}</span>
                <span className="col-span-3 text-slate-400 truncate">{r.buy_venue} → {r.sell_venue}</span>
                <span className="col-span-2 text-right text-slate-300">{fmtP(r.buy)}</span>
                <span className="col-span-2 text-right text-slate-300">{fmtP(r.sell)}</span>
                <span className="col-span-1 text-right text-[#2DD4BF]">{r.spread_pct}%</span>
                <span className="col-span-2 text-right">
                  <span className={`inline-flex items-center gap-1 ${r.status === "EXECUTABLE" ? "text-[#00E676]" : "text-amber-300"}`}>
                    <span className="h-1.5 w-1.5 rounded-full bg-current" /> {r.status}
                  </span>
                </span>
              </div>
            )) : (
              <div className="px-5 py-10 text-center font-mono text-xs text-slate-500">Connecting to live market data…</div>
            )}
          </div>
        </div>
      </div>
      <p className="px-5 py-3 border-t border-white/5 text-[10px] text-slate-600 font-mono">
        {meta.note} Prices are real and read-only; Quantum Scalp does not place live trades in this demo. Spreads reflect momentary price differences between venues.
      </p>
    </div>
  );
}


/* Generic live market endpoint hook */
function useMarket(endpoint, pollMs = 12000) {
  const [state, setState] = useState({ pairs: [], updatedAt: 0, live: false, meta: {} });
  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const { data } = await api.get(endpoint);
        if (alive && data?.pairs) setState({ pairs: data.pairs, updatedAt: data.updated_at, live: data.source === "live", meta: data });
      } catch { /* keep last */ }
    };
    run();
    const t = setInterval(run, pollMs);
    return () => { alive = false; clearInterval(t); };
  }, [endpoint, pollMs]);
  return state;
}

function TerminalShell({ testid, title, sub, badge = "Real Prices · Simulated Execution", live, updatedAt, children, footer }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-white/5 bg-[#0A0B10] overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3.5 border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <span className="qs-pulse-dot h-2 w-2 rounded-full bg-[#00E676] text-[#00E676]" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-white">{title}</span>
          {sub && <span className="font-mono text-[10px] text-slate-500">· {sub}{live && updatedAt ? ` · ${agoStr(updatedAt)}` : ""}</span>}
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest rounded-full border border-[#00E676]/40 bg-[#00E676]/10 px-2.5 py-1 text-[#00E676]">{badge}</span>
      </div>
      <div className="overflow-x-auto"><div className="min-w-[720px]">{children}</div></div>
      {footer && <p className="px-5 py-3 border-t border-white/5 text-[10px] text-slate-600 font-mono">{footer}</p>}
    </div>
  );
}

/* DEX terminal — real Uniswap on-chain vs CEX spreads */
export function DexTerminal() {
  const { pairs, updatedAt, live } = useMarket("/market/dex", 12000);
  return (
    <TerminalShell testid="trading-terminal-dex" title="DEX · CEX↔DEX Spreads" sub="Uniswap v3 · OKX · Ethereum" live={live} updatedAt={updatedAt}
      footer="Real on-chain Uniswap v3 prices (GeckoTerminal) vs centralized-exchange prices. Read-only; Quantum Scalp does not place live trades in this demo.">
      <div className="grid grid-cols-12 gap-2 px-5 py-2.5 border-b border-white/5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
        <span className="col-span-2">Asset</span>
        <span className="col-span-3 text-right">Uniswap (DEX)</span>
        <span className="col-span-3 text-right">OKX (CEX)</span>
        <span className="col-span-2 text-right">Spread</span>
        <span className="col-span-2 text-right">Route</span>
      </div>
      <div className="divide-y divide-white/5">
        {pairs.length ? pairs.map((r) => (
          <div key={r.pair} className="grid grid-cols-12 gap-2 px-5 py-3 items-center font-mono text-xs">
            <span className="col-span-2 text-white">{r.pair}</span>
            <span className="col-span-3 text-right text-slate-300">{fmtP(r.dex_price)}</span>
            <span className="col-span-3 text-right text-slate-300">{fmtP(r.cex_price)}</span>
            <span className="col-span-2 text-right text-[#2DD4BF]">{r.spread_pct}%</span>
            <span className="col-span-2 text-right text-slate-400 truncate">{r.buy_venue}→{r.sell_venue}</span>
          </div>
        )) : <div className="px-5 py-10 text-center font-mono text-xs text-slate-500">Connecting to on-chain data…</div>}
      </div>
    </TerminalShell>
  );
}

const fundClock = (ms) => {
  if (!ms) return "—";
  const s = Math.max(0, Math.floor((ms - Date.now()) / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
};

/* Futures terminal — real perpetual basis + funding */
export function FuturesTerminal() {
  const { pairs, updatedAt, live } = useMarket("/market/futures", 12000);
  return (
    <TerminalShell testid="trading-terminal-futures" title="Futures · Perpetual Basis & Funding" sub="OKX Perpetual Swaps" live={live} updatedAt={updatedAt}
      footer="Real perpetual index/basis and funding rates from OKX public API. Read-only; Quantum Scalp does not place live trades in this demo.">
      <div className="grid grid-cols-12 gap-2 px-5 py-2.5 border-b border-white/5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
        <span className="col-span-2">Contract</span>
        <span className="col-span-2 text-right">Perp</span>
        <span className="col-span-2 text-right">Index</span>
        <span className="col-span-2 text-right">Basis</span>
        <span className="col-span-2 text-right">Funding / APR</span>
        <span className="col-span-2 text-right">Next</span>
      </div>
      <div className="divide-y divide-white/5">
        {pairs.length ? pairs.map((r) => (
          <div key={r.pair} className="grid grid-cols-12 gap-2 px-5 py-3 items-center font-mono text-xs">
            <span className="col-span-2 text-white">{r.pair}</span>
            <span className="col-span-2 text-right text-slate-300">{fmtP(r.perp)}</span>
            <span className="col-span-2 text-right text-slate-400">{fmtP(r.index)}</span>
            <span className={`col-span-2 text-right ${r.basis_pct >= 0 ? "text-[#00E676]" : "text-amber-300"}`}>{r.basis_pct >= 0 ? "+" : ""}{r.basis_pct}%</span>
            <span className={`col-span-2 text-right ${r.funding_pct >= 0 ? "text-[#2DD4BF]" : "text-[#FF2E63]"}`}>{r.funding_pct}% <span className="text-slate-600">/ {r.funding_apr_pct}%</span></span>
            <span className="col-span-2 text-right text-slate-500">{fundClock(r.next_funding)}</span>
          </div>
        )) : <div className="px-5 py-10 text-center font-mono text-xs text-slate-500">Connecting to derivatives data…</div>}
      </div>
    </TerminalShell>
  );
}

